<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type { IBankingBlockResponseMap, IBankingLiquidityPoint } from '../types'
import { useBankingNumber } from '../useBankingNumber'
import WidgetBankingStatsBlock from '../WidgetBankingStatsBlock.vue'

const BALANCES_COLOR = '#2563eb'
const REQUIRED_COLOR = '#374151'
const EXCESS_COLOR = '#16a34a'
const SHORTFALL_COLOR = '#dc2626'
const FILL_OPACITY = 0.3

const { t } = useI18n()
const { formatSom, formatBillions, formatDate } = useBankingNumber()

const { data: response } = useApi<IBankingBlockResponseMap['liquidity']>('/banking-statistics/blocks/liquidity', {
  key: 'banking-statistics-liquidity',
  lazy: true
})

const points = computed<IBankingLiquidityPoint[]>(() => response.value?.data.points ?? [])
const asOf = computed(() => points.value.at(-1)?.period ?? null)

const title = computed<{ key: string; params: Record<string, string | number> }>(() => {
  const last = points.value.at(-1)

  if (!last || last.balances === null || last.required === null) {
    return { key: 'banking_statistics.liquidity.title_neutral', params: {} }
  }

  return {
    key: 'banking_statistics.liquidity.title',
    params: {
      date: formatDate(last.period),
      balances: formatSom(last.balances),
      required: formatSom(last.required)
    }
  }
})

const isFirstOfMonth = (index: number): boolean => {
  const period = points.value[index]?.period
  const previous = points.value[index - 1]?.period

  return period !== undefined && previous !== undefined && period.slice(0, 7) !== previous.slice(0, 7)
}

const lineLabel = (key: 'balances' | 'required' | 'excess'): string => t(`banking_statistics.liquidity.${key}`)

/**
 * The gap between the two lines is filled by two stacks that both start on the required line
 * (an invisible copy of it): one adds the excess where it is positive, so its area spans up to
 * the balances, the other adds the shortfall where it is negative, so its area spans down.
 * Each stack therefore paints only the days of its own sign, in its own colour.
 */
const chartOption = computed<EChartsOption>(() => {
  const required = points.value.map((point) => point.required)
  const excess = points.value.map((point) => (point.excess !== null && point.excess > 0 ? point.excess : 0))
  const shortfall = points.value.map((point) => (point.excess !== null && point.excess < 0 ? point.excess : 0))

  const base = (stack: string) => ({
    type: 'line' as const,
    stack,
    stackStrategy: 'all' as const,
    data: required,
    showSymbol: false,
    silent: true,
    lineStyle: { opacity: 0 },
    tooltip: { show: false }
  })

  const fill = (stack: string, data: number[], color: string) => ({
    type: 'line' as const,
    stack,
    stackStrategy: 'all' as const,
    data,
    showSymbol: false,
    silent: true,
    lineStyle: { opacity: 0 },
    areaStyle: { color, opacity: FILL_OPACITY },
    tooltip: { show: false }
  })

  return {
    grid: { left: 8, right: 16, top: 72, bottom: 8, containLabel: true },
    legend: {
      top: 0,
      left: 0,
      // Hiding a line would leave its green and red fill on the chart.
      selectedMode: false,
      itemWidth: 16,
      textStyle: { fontSize: 12 },
      data: [lineLabel('balances'), lineLabel('required')]
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: points.value.map((point) => formatDate(point.period)),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      // Labels "DD.MM.YYYY": the first day of each month in the data, shown as "MM.YYYY".
      axisLabel: {
        fontSize: 11,
        color: '#6b7280',
        hideOverlap: true,
        interval: (index: number) => isFirstOfMonth(index),
        formatter: (value: string) => value.slice(3)
      }
    },
    yAxis: {
      type: 'value',
      scale: true,
      name: t('banking_statistics.unit_bn'),
      nameTextStyle: { fontSize: 11, color: '#6b7280', align: 'left' },
      axisLabel: { fontSize: 11, color: '#6b7280', formatter: (value: number) => formatBillions(value) },
      splitLine: { lineStyle: { color: '#f3f4f6' } }
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params: unknown) => {
        const index = (params as { dataIndex?: number }[])[0]?.dataIndex
        const point = index === undefined ? undefined : points.value[index]

        if (!point) return ''

        const rows = (['balances', 'required', 'excess'] as const)
          .map((key) => {
            const value = point[key]

            return value === null ? '' : `${lineLabel(key)}: <strong>${formatSom(value)}</strong>`
          })
          .filter(Boolean)

        return `<strong>${formatDate(point.period)}</strong><br/>${rows.join('<br/>')}`
      }
    },
    series: [
      base('excess'),
      fill('excess', excess, EXCESS_COLOR),
      base('shortfall'),
      fill('shortfall', shortfall, SHORTFALL_COLOR),
      {
        type: 'line',
        name: lineLabel('balances'),
        data: points.value.map((point) => point.balances),
        showSymbol: false,
        color: BALANCES_COLOR,
        lineStyle: { width: 1.5, color: BALANCES_COLOR }
      },
      {
        type: 'line',
        name: lineLabel('required'),
        data: required,
        showSymbol: false,
        color: REQUIRED_COLOR,
        lineStyle: { width: 1.5, color: REQUIRED_COLOR, type: 'dashed' }
      }
    ]
  }
})
</script>

<template>
  <WidgetBankingStatsBlock id="liquidity" :title-key="title.key" :title-params="title.params" :as-of="asOf">
    <p class="text-muted-foreground mb-3 text-sm">{{ t('banking_statistics.liquidity.explanation') }}</p>
    <LazyVChart :option="chartOption" autoresize class="h-72 w-full sm:h-80" />
  </WidgetBankingStatsBlock>
</template>
